import React, { useState, useEffect, useMemo } from 'react';
import { 
  ChevronLeft, ChevronRight, ChevronDown, Calendar, TrendingUp, Clock, 
  DollarSign, MapPin, Navigation, Loader, AlertCircle,
  Sparkles, Route, Sun, Cloud, CloudRain, Zap, Target,
  ArrowRight, CheckCircle, Plus, HelpCircle, Lock, Star
} from 'lucide-react';
import routingService from '../../services/routingService';
import { formatCurrency } from '../../utils/helpers';
import { useBusinessContext } from '../../contexts/BusinessContext';

// ============================================
// HELPERS
// ============================================

const getLocalDateString = (date = new Date()) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const getWeekDates = (startDate) => {
  const dates = [];
  const start = new Date(startDate);
  // Adjust to Monday
  const day = start.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + diff);
  
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    dates.push(getLocalDateString(d));
  }
  return dates;
};

const formatDayName = (dateStr) => {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short' });
};

const formatDayDate = (dateStr) => {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getJobDate = (job) => {
  if (!job.scheduledDate) return null;
  return job.scheduledDate.split('T')[0];
};

const getJobTime = (job) => {
  if (!job.scheduledDate) return null;
  const timePart = job.scheduledDate.split('T')[1];
  if (!timePart) return null;
  const [hours, minutes] = timePart.split(':');
  const h = parseInt(hours);
  const m = parseInt(minutes);
  if (h === 0 && m === 0) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Format time string (HH:MM) to display format (9:30 AM)
const formatTimeDisplay = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

// Check if a date is in the past
const isPastDate = (dateStr) => {
  const today = getLocalDateString();
  return dateStr < today;
};

// ============================================
// UPGRADE PROMPT FOR SOLO USERS
// ============================================

const UpgradePrompt = ({ onUpgrade }) => {
  return (
    <div className="max-w-2xl mx-auto">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 via-blue-500 to-teal-500 rounded-2xl p-6 md:p-8 text-white text-center mb-6">
        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Route size={32} />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold mb-2">SuddyRoute™ Week Planner</h2>
        <p className="text-blue-100 text-lg">
          Optimize your routes across the entire week
        </p>
      </div>

      {/* Benefits */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Star size={18} className="text-amber-500" />
          What you'll get with Pro
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp size={18} className="text-green-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Revenue-Optimized Routes</p>
              <p className="text-sm text-gray-500">Automatically sequence jobs to maximize your dollars per hour</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Clock size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Real-Time Traffic</p>
              <p className="text-sm text-gray-500">Routes calculated with live traffic data, not just distances</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Calendar size={18} className="text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Week-at-a-Glance Planning</p>
              <p className="text-sm text-gray-500">See optimization potential across all 7 days at once</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Target size={18} className="text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Identify Light Days</p>
              <p className="text-sm text-gray-500">Spot scheduling gaps perfect for sales prospecting</p>
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof / Stats */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
        <p className="text-green-800 text-center">
          <span className="font-semibold">Pro users save an average of 45 minutes per day</span>
          <br />
          <span className="text-sm text-green-600">That's nearly 4 hours a week back in your pocket</span>
        </p>
      </div>

      {/* CTA */}
      <div className="text-center">
        <button
          onClick={onUpgrade}
          className="bg-gradient-to-r from-blue-600 to-teal-500 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl hover:opacity-90 transition-all inline-flex items-center gap-2"
        >
          <Sparkles size={18} />
          Upgrade to Pro - $49/mo
        </button>
        <p className="text-sm text-gray-500 mt-3">
          Cancel anytime • 14-day money-back guarantee
        </p>
      </div>
    </div>
  );
};

// ============================================
// DAY CARD COMPONENT
// ============================================

const DayCard = ({ 
  date, 
  jobs, 
  analysis, 
  isToday, 
  isPast,
  isAnalyzing,
  onAnalyze,
  onNavigate,
  onDayClick,
  onJobClick,
  isOwner
}) => {
  const dayName = formatDayName(date);
  const dayDate = formatDayDate(date);
  const jobCount = jobs.length;
  const scheduledJobs = jobs.filter(j => j.status === 'Scheduled' || j.status === 'In Progress');
  const totalRevenue = jobs.reduce((sum, j) => sum + (j.quoteAmount || 0), 0);
  const totalDuration = jobs.reduce((sum, j) => sum + (j.estimatedDurationMinutes || 60), 0);
  
  // Determine day status
  const isEmpty = jobCount === 0;
  const isLight = jobCount > 0 && jobCount <= 2;
  const isBusy = jobCount >= 5;
  const hasOverdueJobs = isPast && scheduledJobs.length > 0;
  
  // Get revenue per hour from analysis or estimate
  const revenuePerHour = analysis?.revenuePerHour || (totalDuration > 0 ? (totalRevenue / (totalDuration / 60)) : 0);
  
  // Past empty days - minimal display
  if (isPast && isEmpty) {
    return (
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 opacity-50">
        <div className="text-center">
          <span className="text-sm font-medium text-gray-400">{dayName}</span>
          <p className="text-xs text-gray-400">{dayDate}</p>
        </div>
      </div>
    );
  }
  
  // Past days with jobs - warning state with clickable jobs
  if (hasOverdueJobs) {
    return (
      <div className="bg-amber-50 rounded-xl border-2 border-amber-300">
        {/* Day Header */}
        <div className="px-4 py-3 border-b border-amber-200 bg-amber-100/50 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-lg font-bold text-amber-800">{dayName}</span>
              <p className="text-sm text-amber-600">{dayDate}</p>
            </div>
            <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full flex items-center gap-1">
              <AlertCircle size={12} />
              Overdue
            </span>
          </div>
        </div>
        
        {/* Clickable Job List */}
        <div className="p-3 space-y-2">
          {[...scheduledJobs]
            .sort((a, b) => {
              const timeA = getJobTime(a) || '99:99';
              const timeB = getJobTime(b) || '99:99';
              return timeA.localeCompare(timeB);
            })
            .slice(0, 3)
            .map((job) => {
              const jobTime = getJobTime(job);
              const timeDisplay = jobTime ? formatTimeDisplay(jobTime) : null;
              return (
                <button
                  key={job.id}
                  onClick={() => onJobClick?.(job)}
                  className="w-full text-left p-2 bg-white rounded-lg border border-amber-200 hover:border-amber-400 hover:shadow-sm transition-all"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 text-sm truncate">
                      {job.customer?.name || 'Unknown'}
                    </span>
                    {isOwner && job.quoteAmount > 0 && (
                      <span className="text-sm font-medium text-amber-700">
                        {formatCurrency(job.quoteAmount)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-xs text-gray-500 truncate">{job.address}</p>
                    {timeDisplay && (
                      <span className="text-xs text-amber-600 font-medium flex-shrink-0 ml-2">
                        {timeDisplay}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          {scheduledJobs.length > 3 && (
            <button
              onClick={() => onDayClick?.(date)}
              className="w-full text-center text-xs text-amber-700 hover:text-amber-800 py-1"
            >
              +{scheduledJobs.length - 3} more job{scheduledJobs.length - 3 !== 1 ? 's' : ''}
            </button>
          )}
        </div>
        
        {/* Summary Footer */}
        {isOwner && totalRevenue > 0 && (
          <div className="px-4 pb-3 pt-1 border-t border-amber-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-amber-700">Total pending</span>
              <span className="font-semibold text-amber-800">{formatCurrency(totalRevenue)}</span>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div 
      className={`
        bg-white rounded-xl border-2 transition-all cursor-pointer hover:shadow-md
        ${isToday ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'}
        ${isEmpty ? 'opacity-60' : ''}
        ${isPast ? 'opacity-75' : ''}
      `}
      onClick={() => onDayClick?.(date)}
    >
      {/* Day Header */}
      <div className={`
        px-4 py-3 border-b rounded-t-xl
        ${isToday ? 'bg-blue-50' : 'bg-gray-50'}
      `}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className={`text-lg font-bold ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                {dayName}
              </span>
              {isToday && (
                <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                  Today
                </span>
              )}
            </div>
            <span className="text-sm text-gray-500">{dayDate}</span>
          </div>
          
          {/* Status Badge */}
          {isEmpty ? (
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
              No jobs
            </span>
          ) : isLight ? (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full flex items-center gap-1">
              <Target size={12} />
              Light day
            </span>
          ) : isBusy ? (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
              <Zap size={12} />
              Busy!
            </span>
          ) : (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
              {jobCount} jobs
            </span>
          )}
        </div>
      </div>
      
      {/* Day Stats */}
      <div className="p-4">
        {isEmpty ? (
          <div className="text-center py-4">
            <Calendar size={24} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-400">Schedule some jobs!</p>
            <p className="text-xs text-gray-400 mt-1">Great day for sales calls</p>
          </div>
        ) : (
          <>
            {/* Clickable Job List - show optimized order if available, otherwise scheduled time */}
            {/* Show ALL jobs for today, limit to 3 for other days */}
            <div className="space-y-2 mb-3">
              {(() => {
                const maxJobs = isToday ? 999 : 3; // Show all for today
                
                // If we have analysis with optimized jobs, use that order
                if (analysis?.optimizedJobs?.length > 0) {
                  return analysis.optimizedJobs.slice(0, maxJobs).map((optJob, index) => {
                    // Find the full job object to get customer info
                    const job = scheduledJobs.find(j => j.id === optJob.jobId) || optJob;
                    const jobTime = getJobTime(job);
                    const timeDisplay = jobTime ? formatTimeDisplay(jobTime) : null;
                    return (
                      <button
                        key={optJob.jobId || job.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onJobClick?.(job);
                        }}
                        className="w-full text-left p-2 bg-emerald-50 rounded-lg border border-emerald-200 hover:border-emerald-400 hover:shadow-sm transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900 text-sm truncate">
                                {optJob.customerName || job.customer?.name || 'Unknown'}
                              </span>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {isOwner && (optJob.quoteAmount || job.quoteAmount) > 0 && (
                                  <span className="text-sm font-medium text-green-600">
                                    {formatCurrency(optJob.quoteAmount || job.quoteAmount)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-0.5">
                              <p className="text-xs text-gray-500 truncate">{optJob.address || job.address}</p>
                              {timeDisplay && (
                                <span className="text-xs text-emerald-600 font-medium flex-shrink-0 ml-2">
                                  {timeDisplay}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  });
                }
                
                // Otherwise show in scheduled time order
                return [...scheduledJobs]
                  .sort((a, b) => {
                    const timeA = getJobTime(a) || '99:99';
                    const timeB = getJobTime(b) || '99:99';
                    return timeA.localeCompare(timeB);
                  })
                  .slice(0, maxJobs)
                  .map((job) => {
                    const jobTime = getJobTime(job);
                    const timeDisplay = jobTime ? formatTimeDisplay(jobTime) : null;
                    return (
                      <button
                        key={job.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onJobClick?.(job);
                        }}
                        className="w-full text-left p-2 bg-gray-50 rounded-lg border border-gray-100 hover:border-blue-300 hover:bg-blue-50 hover:shadow-sm transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900 text-sm truncate">
                            {job.customer?.name || 'Unknown'}
                          </span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {isOwner && job.quoteAmount > 0 && (
                              <span className="text-sm font-medium text-green-600">
                                {formatCurrency(job.quoteAmount)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className="text-xs text-gray-500 truncate">{job.address}</p>
                          {timeDisplay && (
                            <span className="text-xs text-blue-600 font-medium flex-shrink-0 ml-2">
                              {timeDisplay}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  });
              })()}
              {!isToday && (analysis?.optimizedJobs?.length > 3 || (!analysis && scheduledJobs.length > 3)) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDayClick?.(date);
                  }}
                  className="w-full text-center text-xs text-blue-600 hover:text-blue-700 py-1"
                >
                  +{(analysis?.optimizedJobs?.length || scheduledJobs.length) - 3} more job{((analysis?.optimizedJobs?.length || scheduledJobs.length) - 3) !== 1 ? 's' : ''}
                </button>
              )}
            </div>
            
            {/* Analysis Results or Optimize Button - Only for current/future */}
            {analysis ? (
              <div className="space-y-2 pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between text-xs text-emerald-600 font-medium mb-1">
                  <span className="flex items-center gap-1">
                    <Sparkles size={12} />
                    Optimized Route
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-1">
                    <Route size={14} />
                    Distance
                  </span>
                  <span className="font-medium">{analysis.totalDistance?.toFixed(1)} mi</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-1">
                    <Clock size={14} />
                    Drive time
                  </span>
                  <span className="font-medium">{Math.round(analysis.totalDriveMinutes || 0)} min</span>
                </div>
                {isOwner && (
                  <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-100">
                    <span className="text-gray-500 flex items-center gap-1">
                      <TrendingUp size={14} />
                      $/hour
                    </span>
                    <span className="font-bold text-green-600">{formatCurrency(analysis.revenuePerHour)}</span>
                  </div>
                )}
              </div>
            ) : !isPast && scheduledJobs.length >= 2 ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAnalyze?.(date);
                }}
                disabled={isAnalyzing}
                className="w-full bg-gradient-to-r from-blue-500 to-teal-500 text-white text-sm font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isAnalyzing ? (
                  <>
                    <Loader size={14} className="animate-spin" />
                    Optimizing...
                  </>
                ) : (
                  <>
                    <Sparkles size={14} />
                    Optimize
                  </>
                )}
              </button>
            ) : (
              <div className="text-center text-xs text-gray-400 py-2">
                {scheduledJobs.length === 1 ? '1 job - add more to optimize' : 'No scheduled jobs'}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Action Footer - Only for current/future days */}
      {!isEmpty && !isPast && scheduledJobs.length > 0 && (
        <div className="px-4 pb-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNavigate?.(date);
            }}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm font-medium py-2.5 px-3 rounded-lg flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
          >
            <Navigation size={14} />
            Navigate
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================
// WEEK SUMMARY BANNER
// ============================================

const WeekSummaryBanner = ({ weekAnalysis, isOwner }) => {
  if (!weekAnalysis) return null;
  
  const { 
    totalJobs, 
    totalRevenue, 
    averageRevenuePerHour, 
    bestDay, 
    lightDays,
    totalDistance,
    totalDriveTime 
  } = weekAnalysis;
  
  return (
    <div className="bg-gradient-to-r from-blue-600 via-blue-500 to-teal-500 rounded-xl p-4 text-white mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2">
          <TrendingUp size={18} />
          Week Overview
        </h3>
        {lightDays > 0 && (
          <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
            {lightDays} light day{lightDays !== 1 ? 's' : ''} - room for more!
          </span>
        )}
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white/10 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{totalJobs}</div>
          <div className="text-xs text-white/80">Total Jobs</div>
        </div>
        {isOwner && (
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold">{formatCurrency(totalRevenue)}</div>
            <div className="text-xs text-white/80">Revenue</div>
          </div>
        )}
        <div className="bg-white/10 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{totalDistance?.toFixed(0) || '—'}</div>
          <div className="text-xs text-white/80">Miles</div>
        </div>
        {isOwner && (
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold">{formatCurrency(averageRevenuePerHour || 0)}</div>
            <div className="text-xs text-white/80">Avg $/hr</div>
          </div>
        )}
      </div>
      
      {bestDay && isOwner && (
        <div className="mt-3 pt-3 border-t border-white/20 text-sm flex items-center gap-2">
          <CheckCircle size={14} />
          <span>Best day: <strong>{formatDayName(bestDay.date)}</strong> at {formatCurrency(bestDay.revenuePerHour)}/hr</span>
        </div>
      )}
    </div>
  );
};

// ============================================
// SMART SUGGESTIONS
// ============================================

const SmartSuggestions = ({ jobsByDate, weekDates, dayAnalyses, isOwner, onDayClick }) => {
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default
  const todayStr = getLocalDateString();
  
  // Generate suggestions based on the week's data
  const suggestions = useMemo(() => {
    const tips = [];
    
    // Analyze each day
    const dayStats = weekDates.map(date => {
      const jobs = jobsByDate[date] || [];
      const scheduledJobs = jobs.filter(j => j.status === 'Scheduled' || j.status === 'In Progress');
      const totalRevenue = scheduledJobs.reduce((sum, j) => sum + (j.quoteAmount || 0), 0);
      const analysis = dayAnalyses[date];
      const isPast = date < todayStr;
      
      // Extract cities from jobs
      const cities = {};
      scheduledJobs.forEach(job => {
        const city = job.city || 'Unknown';
        if (!cities[city]) cities[city] = [];
        cities[city].push(job);
      });
      
      return {
        date,
        dayName: formatDayName(date),
        jobCount: scheduledJobs.length,
        revenue: totalRevenue,
        revenuePerHour: analysis?.revenuePerHour || 0,
        cities,
        isPast,
        jobs: scheduledJobs,
      };
    });
    
    // Filter to current/future days only
    const futureDays = dayStats.filter(d => !d.isPast);
    
    // 1. GEOGRAPHIC CLUSTERING - Find days with jobs clustered in same city
    futureDays.forEach(day => {
      const cityEntries = Object.entries(day.cities);
      cityEntries.forEach(([city, cityJobs]) => {
        if (cityJobs.length >= 3 && city !== 'Unknown') {
          tips.push({
            type: 'cluster',
            icon: '📍',
            priority: 2,
            title: `${cityJobs.length} jobs clustered in ${city}`,
            description: `${day.dayName} has ${cityJobs.length} jobs in ${city} - great for efficiency!`,
            action: null,
            date: day.date,
          });
        }
      });
    });
    
    // 2. GEOGRAPHIC SPREAD - Find days with jobs spread across many cities
    futureDays.forEach(day => {
      const uniqueCities = Object.keys(day.cities).filter(c => c !== 'Unknown');
      if (day.jobCount >= 3 && uniqueCities.length >= 3 && uniqueCities.length >= day.jobCount * 0.8) {
        tips.push({
          type: 'spread',
          icon: '🗺️',
          priority: 3,
          title: `${day.dayName}'s jobs are spread out`,
          description: `${day.jobCount} jobs across ${uniqueCities.length} different areas - expect more drive time`,
          action: 'Consider tighter geographic scheduling',
          date: day.date,
        });
      }
    });
    
    // 3. LIGHT DAY OPPORTUNITIES
    futureDays.forEach(day => {
      if (day.jobCount === 0) {
        // Find busiest area this week
        const allCities = {};
        futureDays.forEach(d => {
          Object.entries(d.cities).forEach(([city, jobs]) => {
            if (city !== 'Unknown') {
              allCities[city] = (allCities[city] || 0) + jobs.length;
            }
          });
        });
        const busiestCity = Object.entries(allCities).sort((a, b) => b[1] - a[1])[0];
        
        tips.push({
          type: 'empty',
          icon: '📅',
          priority: 1,
          title: `${day.dayName} is wide open`,
          description: busiestCity 
            ? `Great day for prospecting - your busiest area this week is ${busiestCity[0]}`
            : `No jobs scheduled - perfect for sales calls or admin work`,
          action: 'Book some jobs!',
          date: day.date,
        });
      } else if (day.jobCount === 1) {
        const singleJob = day.jobs[0];
        const jobCity = singleJob?.city || 'the area';
        tips.push({
          type: 'light',
          icon: '💡',
          priority: 2,
          title: `${day.dayName} has only 1 job`,
          description: `You're already going to ${jobCity} - try to book nearby jobs`,
          action: 'Check for leads in the area',
          date: day.date,
        });
      }
    });
    
    // 4. WORKLOAD IMBALANCE
    const jobCounts = futureDays.map(d => d.jobCount).filter(c => c > 0);
    if (jobCounts.length >= 2) {
      const maxJobs = Math.max(...jobCounts);
      const minJobs = Math.min(...jobCounts);
      
      if (maxJobs >= 5 && minJobs <= 2 && maxJobs - minJobs >= 3) {
        const busiestDay = futureDays.find(d => d.jobCount === maxJobs);
        const lightestDay = futureDays.find(d => d.jobCount === minJobs && d.jobCount > 0);
        
        if (busiestDay && lightestDay) {
          tips.push({
            type: 'balance',
            icon: '⚖️',
            priority: 1,
            title: 'Workload is unbalanced',
            description: `${busiestDay.dayName} has ${busiestDay.jobCount} jobs while ${lightestDay.dayName} only has ${lightestDay.jobCount}`,
            action: 'Consider moving a job to balance your week',
            date: busiestDay.date,
          });
        }
      }
    }
    
    // 5. REVENUE OPTIMIZATION - Compare $/hr across analyzed days
    if (isOwner) {
      const analyzedDays = futureDays.filter(d => d.revenuePerHour > 0);
      if (analyzedDays.length >= 2) {
        const avgRevenuePerHour = analyzedDays.reduce((sum, d) => sum + d.revenuePerHour, 0) / analyzedDays.length;
        
        analyzedDays.forEach(day => {
          if (day.revenuePerHour < avgRevenuePerHour * 0.7) {
            tips.push({
              type: 'revenue',
              icon: '💰',
              priority: 2,
              title: `${day.dayName} has lower $/hr`,
              description: `${formatCurrency(day.revenuePerHour)}/hr vs ${formatCurrency(avgRevenuePerHour)}/hr average`,
              action: 'Jobs may be too spread out or lower value',
              date: day.date,
            });
          }
        });
      }
    }
    
    // 6. SAME-CITY JOBS ON DIFFERENT DAYS
    const cityByDay = {};
    futureDays.forEach(day => {
      Object.keys(day.cities).forEach(city => {
        if (city !== 'Unknown') {
          if (!cityByDay[city]) cityByDay[city] = [];
          cityByDay[city].push({ date: day.date, dayName: day.dayName, count: day.cities[city].length });
        }
      });
    });
    
    Object.entries(cityByDay).forEach(([city, days]) => {
      if (days.length >= 2) {
        const totalInCity = days.reduce((sum, d) => sum + d.count, 0);
        if (totalInCity >= 3) {
          // Check if one day has significantly more
          const maxDay = days.reduce((max, d) => d.count > max.count ? d : max, days[0]);
          const otherDays = days.filter(d => d.date !== maxDay.date);
          
          if (maxDay.count >= 2 && otherDays.some(d => d.count === 1)) {
            const singleDay = otherDays.find(d => d.count === 1);
            tips.push({
              type: 'consolidate',
              icon: '🔄',
              priority: 1,
              title: `Consolidate ${city} jobs?`,
              description: `${maxDay.dayName} has ${maxDay.count} jobs in ${city}, but ${singleDay.dayName} has just 1`,
              action: `Consider moving to ${maxDay.dayName} to reduce travel`,
              date: singleDay.date,
            });
          }
        }
      }
    });
    
    // Sort by priority and limit
    return tips.sort((a, b) => a.priority - b.priority).slice(0, 4);
  }, [jobsByDate, weekDates, dayAnalyses, isOwner, todayStr]);
  
  // Don't render if no suggestions
  if (suggestions.length === 0) return null;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 mb-4 overflow-hidden">
      {/* Header - Clickable to expand/collapse */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-xl">💡</span>
          <h3 className="font-semibold text-gray-900">Smart Suggestions</h3>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
            {suggestions.length}
          </span>
        </div>
        <ChevronDown 
          size={20} 
          className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {/* Suggestions List */}
      {isExpanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {suggestions.map((suggestion, index) => (
            <div 
              key={index}
              className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => suggestion.date && onDayClick?.(suggestion.date)}
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">{suggestion.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{suggestion.title}</p>
                  <p className="text-sm text-gray-600 mt-0.5">{suggestion.description}</p>
                  {suggestion.action && (
                    <p className="text-sm text-blue-600 font-medium mt-1 flex items-center gap-1">
                      <ArrowRight size={14} />
                      {suggestion.action}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// HELP MODAL
// ============================================

const HelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-teal-500 text-white px-4 py-3 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Sparkles size={18} />
              Route Optimization Guide
            </h3>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-4 space-y-4">
          {/* How It Works */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Route size={16} className="text-blue-500" />
              How It Works
            </h4>
            <p className="text-sm text-gray-600">
              SuddyRoute™ calculates the most efficient route through your jobs using real-time traffic data and actual drive times - not just straight-line distances like free alternatives.
            </p>
          </div>
          
          {/* Optimization Modes */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Target size={16} className="text-blue-500" />
              Optimization Modes
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-green-500 font-bold">💰 Revenue:</span>
                <span className="text-gray-600">Maximizes dollars per hour. Great when you have more jobs than time.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-blue-500 font-bold">⚖️ Balanced:</span>
                <span className="text-gray-600">Best mix of revenue and efficiency. Recommended for most days.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-purple-500 font-bold">🛣️ Distance:</span>
                <span className="text-gray-600">Minimizes miles driven. Use when gas costs matter most.</span>
              </div>
            </div>
          </div>
          
          {/* Navigate vs Optimize */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Navigation size={16} className="text-blue-500" />
              Navigate vs Optimize
            </h4>
            <div className="space-y-2 text-sm text-gray-600">
              <p><strong>Navigate:</strong> Opens Google Maps with your jobs in their <em>scheduled time order</em>. Use when customers have specific appointment times.</p>
              <p><strong>Optimize:</strong> Shows you the most efficient route order first, then you choose. Use when times are flexible.</p>
            </div>
          </div>
          
          {/* Tips */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">💡 Pro Tips</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>Optimize in the morning when traffic data is most relevant</li>
              <li>Light days (1-2 jobs) are great for sales prospecting in that area</li>
              <li>Week view helps identify scheduling gaps</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const MultiDayAnalyzer = ({ 
  jobs, 
  onDayClick,
  onJobClick,
  onNewJob,
  isOwner = true 
}) => {
  const { canAccessFeature, isTrialActive, getTierBadge } = useBusinessContext();
  
  // Check if user can access route optimization (Pro+ feature)
  const hasRouteAccess = canAccessFeature('route-optimization');
  const tierBadge = getTierBadge('route-optimization');

  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    return monday;
  });
  
  const [dayAnalyses, setDayAnalyses] = useState({});
  const [analyzingDay, setAnalyzingDay] = useState(null);
  const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [gpsLocation, setGpsLocation] = useState(null);
  
  const weekDates = useMemo(() => getWeekDates(currentWeekStart), [currentWeekStart]);
  const todayStr = getLocalDateString();
  
  // Track window width for responsive behavior
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Group jobs by date (must be defined before mobileVisibleDates)
  const jobsByDate = useMemo(() => {
    const grouped = {};
    weekDates.forEach(date => {
      grouped[date] = [];
    });
    
    jobs.forEach(job => {
      const jobDate = getJobDate(job);
      if (jobDate && grouped[jobDate] !== undefined) {
        grouped[jobDate].push(job);
      }
    });
    
    return grouped;
  }, [jobs, weekDates]);
  
  // For mobile, only show today+future OR past days that have jobs
  const mobileVisibleDates = useMemo(() => {
    return weekDates.filter(date => {
      // Always show today and future
      if (date >= todayStr) return true;
      // Show past days only if they have jobs
      const dayJobs = jobsByDate[date] || [];
      return dayJobs.length > 0;
    });
  }, [weekDates, todayStr, jobsByDate]);
  
  // Handle upgrade navigation
  const handleUpgrade = () => {
    window.location.href = '/settings?tab=subscription';
  };
  
  // If user doesn't have access (Solo tier, not on trial), show upgrade prompt
  if (!hasRouteAccess) {
    return (
      <div className="py-4">
        <UpgradePrompt onUpgrade={handleUpgrade} />
      </div>
    );
  }

  
  // Calculate week summary
  const weekAnalysis = useMemo(() => {
    let totalJobs = 0;
    let totalRevenue = 0;
    let totalDistance = 0;
    let totalDriveTime = 0;
    let lightDays = 0;
    let bestDay = null;
    let analyzedDays = 0;
    
    weekDates.forEach(date => {
      const dayJobs = jobsByDate[date] || [];
      const dayAnalysis = dayAnalyses[date];
      
      totalJobs += dayJobs.length;
      totalRevenue += dayJobs.reduce((sum, j) => sum + (j.quoteAmount || 0), 0);
      
      if (dayJobs.length > 0 && dayJobs.length <= 2) {
        lightDays++;
      }
      
      if (dayAnalysis) {
        analyzedDays++;
        totalDistance += dayAnalysis.totalDistance || 0;
        totalDriveTime += dayAnalysis.totalDriveMinutes || 0;
        
        if (!bestDay || dayAnalysis.revenuePerHour > bestDay.revenuePerHour) {
          bestDay = { date, revenuePerHour: dayAnalysis.revenuePerHour };
        }
      }
    });
    
    const totalWorkMinutes = jobs.reduce((sum, j) => sum + (j.estimatedDurationMinutes || 60), 0) + totalDriveTime;
    const averageRevenuePerHour = totalWorkMinutes > 0 ? (totalRevenue / (totalWorkMinutes / 60)) : 0;
    
    return {
      totalJobs,
      totalRevenue,
      totalDistance: analyzedDays > 0 ? totalDistance : null,
      totalDriveTime,
      lightDays,
      bestDay,
      averageRevenuePerHour: analyzedDays > 0 ? averageRevenuePerHour : null,
    };
  }, [weekDates, jobsByDate, dayAnalyses]);
  
  // Get GPS on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGpsLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude
          });
        },
        (err) => console.log('GPS not available:', err),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);
  
  // Navigation
  const goToPrevWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
    setDayAnalyses({}); // Clear analyses for new week
  };
  
  const goToNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
    setDayAnalyses({});
  };
  
  const goToThisWeek = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    setCurrentWeekStart(monday);
    setDayAnalyses({});
  };
  
  // Analyze single day
  const analyzeDay = async (date) => {
    const dayJobs = jobsByDate[date] || [];
    const scheduledJobs = dayJobs.filter(j => j.status === 'Scheduled' || j.status === 'In Progress');
    
    if (scheduledJobs.length < 2) return;
    
    setAnalyzingDay(date);
    
    try {
      const response = await routingService.optimizeDay(date, gpsLocation);
      const data = response.data;
      
      setDayAnalyses(prev => ({
        ...prev,
        [date]: {
          totalDistance: data.totalDistance,
          totalDriveMinutes: data.totalDriveMinutes || (data.totalDurationMinutes - dayJobs.reduce((sum, j) => sum + (j.estimatedDurationMinutes || 60), 0)),
          revenuePerHour: data.revenuePerHour,
          optimizedJobs: data.optimizedJobs,
        }
      }));
    } catch (error) {
      console.error('Error analyzing day:', error);
    } finally {
      setAnalyzingDay(null);
    }
  };
  
  // Analyze all days (skip past days)
  const analyzeAllDays = async () => {
    setIsAnalyzingAll(true);
    
    for (const date of weekDates) {
      // Skip past days
      if (isPastDate(date)) continue;
      
      const dayJobs = jobsByDate[date] || [];
      const scheduledJobs = dayJobs.filter(j => j.status === 'Scheduled' || j.status === 'In Progress');
      
      if (scheduledJobs.length >= 2 && !dayAnalyses[date]) {
        await analyzeDay(date);
        // Small delay between API calls
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    
    setIsAnalyzingAll(false);
  };
  
  // Navigate to day's jobs
  const navigateDay = (date) => {
    const dayJobs = jobsByDate[date] || [];
    const scheduledJobs = dayJobs
      .filter(j => j.status === 'Scheduled' || j.status === 'In Progress')
      .sort((a, b) => {
        const timeA = getJobTime(a) || '99:99';
        const timeB = getJobTime(b) || '99:99';
        return timeA.localeCompare(timeB);
      });
    
    if (scheduledJobs.length === 0) return;
    
    // Use analyzed optimized order if available
    const analysis = dayAnalyses[date];
    const orderedJobs = analysis?.optimizedJobs || scheduledJobs;
    
    const addresses = orderedJobs.map(job => {
      const j = job.address ? job : scheduledJobs.find(sj => sj.id === job.jobId);
      const parts = [j?.address, j?.city, j?.state, j?.zipCode].filter(Boolean);
      return encodeURIComponent(parts.join(', '));
    });
    
    if (addresses.length === 1) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${addresses[0]}`, '_blank');
    } else {
      const destination = addresses[addresses.length - 1];
      const waypoints = addresses.slice(0, -1).join('|');
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}&waypoints=${waypoints}`, '_blank');
    }
  };
  
  // Week header display
  const weekDisplay = useMemo(() => {
    const start = new Date(weekDates[0] + 'T12:00:00');
    const end = new Date(weekDates[6] + 'T12:00:00');
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }, [weekDates]);
  
  // Check if any day can be analyzed (only current/future days)
  const hasAnalyzableDays = weekDates.some(date => {
    if (isPastDate(date)) return false; // Don't analyze past days
    const dayJobs = jobsByDate[date] || [];
    const scheduledJobs = dayJobs.filter(j => j.status === 'Scheduled' || j.status === 'In Progress');
    return scheduledJobs.length >= 2 && !dayAnalyses[date];
  });
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-600 to-teal-500 rounded-lg text-white">
              <Calendar size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900">Week Planner</h2>
                <button
                  onClick={() => setShowHelp(true)}
                  className="text-gray-400 hover:text-blue-500 transition-colors"
                  title="How it works"
                >
                  <HelpCircle size={18} />
                </button>
                {isTrialActive && tierBadge && (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${tierBadge.color}`}>
                    {tierBadge.label}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">Optimize your routes across the week</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {hasAnalyzableDays && (
              <button
                onClick={analyzeAllDays}
                disabled={isAnalyzingAll}
                className="bg-gradient-to-r from-blue-600 to-teal-500 text-white text-sm font-medium py-2 px-4 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isAnalyzingAll ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Optimizing...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    Optimize All
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        
        {/* Week Navigation */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <button
            onClick={goToPrevWeek}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          
          <div className="flex items-center gap-3">
            <span className="font-medium text-gray-900">{weekDisplay}</span>
            <button
              onClick={goToThisWeek}
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
            >
              This Week
            </button>
          </div>
          
          <button
            onClick={goToNextWeek}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
      
      {/* Week Summary */}
      <WeekSummaryBanner weekAnalysis={weekAnalysis} isOwner={isOwner} />
      
      {/* Smart Suggestions */}
      <SmartSuggestions 
        jobsByDate={jobsByDate}
        weekDates={weekDates}
        dayAnalyses={dayAnalyses}
        isOwner={isOwner}
        onDayClick={onDayClick}
      />
      
      {/* Day Cards Grid - Desktop shows all, Mobile shows today+ and past days with jobs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {(isMobile ? mobileVisibleDates : weekDates).map(date => (
          <DayCard
            key={date}
            date={date}
            jobs={jobsByDate[date] || []}
            analysis={dayAnalyses[date]}
            isToday={date === todayStr}
            isPast={isPastDate(date)}
            isAnalyzing={analyzingDay === date || isAnalyzingAll}
            onAnalyze={analyzeDay}
            onNavigate={navigateDay}
            onDayClick={onDayClick}
            onJobClick={onJobClick}
            isOwner={isOwner}
          />
        ))}
      </div>
      
      {/* Empty State / Tips */}
      {weekAnalysis.totalJobs === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <Calendar size={40} className="mx-auto text-amber-400 mb-3" />
          <h3 className="font-semibold text-amber-900 mb-1">No jobs scheduled this week</h3>
          <p className="text-sm text-amber-700 mb-4">
            Add some jobs to start planning your routes
          </p>
          {onNewJob && (
            <button
              onClick={() => onNewJob()}
              className="bg-amber-500 text-white font-medium py-2 px-4 rounded-lg inline-flex items-center gap-2 hover:bg-amber-600 transition-colors"
            >
              <Plus size={18} />
              New Job
            </button>
          )}
        </div>
      )}
      
      {/* Help Modal */}
      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
};

export default MultiDayAnalyzer;